import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { TOOL_APPLICABILITY } from '../../core/tools/config/toolApplicability';
import { useTranslation } from '../../hooks/useTranslation';

const presetSizes = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48, 56, 64, 72];

const FontSizeToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const [open, setOpen] = React.useState(false);
  const current = context.selectionStyles?.fontSize ?? 16;
  const [fontSize, setFontSize] = React.useState(current);
  const [customSize, setCustomSize] = React.useState(String(current));
  React.useEffect(() => {
    const next = context.selectionStyles?.fontSize ?? 16;
    setFontSize(next);
    setCustomSize(String(next));
  }, [context.selectionStyles?.fontSize]);
  
  const selectedCount = context.selection.size;
  const isDisabled = selectedCount === 0;

  const handleSizeChange = (size: number) => {
    setFontSize(size);
    setCustomSize(size.toString());
    onExecute({ fontSize: size });
    setOpen(false);
  };

  const handleCustomSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomSize(value);
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0 && numValue <= 200) {
      setFontSize(numValue);
    }
  };

  const handleCustomSizeApply = () => {
    const numValue = parseInt(customSize, 10);
    if (!isNaN(numValue) && numValue > 0 && numValue <= 200) {
      handleSizeChange(numValue);
    }
  };


  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tid.tools.fontSize.trigger"
          disabled={isDisabled}
          className="px-2.5 h-8 min-w-[36px] hover:bg-muted rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t('tools.fontSize')}
        >
          <span className="text-sm font-medium text-foreground">{fontSize}</span>
        </button>
      </PopoverTrigger>
        <PopoverContent className="w-[200px] p-3" align="start">
          <div className="space-y-3">
            {/* Custom input */}
            <div className="flex items-center gap-2">
              <Input
                type="number"
                data-testid="tid.tools.fontSize.input"
                value={customSize}
                onChange={handleCustomSizeChange}
                className="h-8 flex-1"
                placeholder={t('properties.size')}
                min="8"
                max="200"
              />
              <Button
                size="sm"
                data-testid="tid.tools.fontSize.apply"
                onClick={handleCustomSizeApply}
                className="h-8 px-3"
              >
                {t('properties.apply')}
              </Button>
            </div>
            
            {/* Preset sizes */}
            <div className="grid grid-cols-4 gap-1">
              {presetSizes.map((size) => (
                <Button
                  key={size}
                  data-testid={`tid.tools.fontSize.preset.${size}`}
                  variant={fontSize === size ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-8 p-0 text-xs font-medium"
                  onClick={() => handleSizeChange(size)}
                >
                  {size}
                </Button>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
  );
};

export class FontSizeTool extends BaseTool {
  private hasOrgChartSelection(context: IToolContext): boolean {
    const selectedIds = Array.from(context.selection);
    if (selectedIds.length === 0) return false;
    return selectedIds.some((id) => {
      const shape = context.shapes.get(id) as any;
      const data = shape?.data ?? shape;
      const category = data?.category ?? data?.attrs?.category;
      const type = data?.type as string | undefined;
      return category === 'orgchart' || (type ? type.startsWith('org_') : false);
    });
  }

  manifest: IToolManifest = {
    id: 'font-size',
    name: 'tools.fontSize',
    icon: 'type',
    category: 'style',
    description: 'Change font size of selected text',
    version: '1.0.0',
    applicability: TOOL_APPLICABILITY.TEXT_SHAPES,
    
    requiresSelection: true,
    minSelection: 1,
    
    ui: {
      toolbar: true,
      contextMenu: false,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    }
  };

  canExecute(context: IToolContext): boolean {
    return super.canExecute(context) && context.selection.size > 0 && !this.hasOrgChartSelection(context);
  }

  execute(params?: { fontSize: number }): ICommand | null {
    if (!this.context || this.context.selection.size === 0 || !params?.fontSize) {
      return null;
    }

    const selectedIds = Array.from(this.context.selection);
    return new ChangeNodeStyleCommand(selectedIds, 'fontSize', params.fontSize);
  }

  getToolbarComponent() {
    return FontSizeToolComponent;
  }

  getFloatingPanelComponent() {
    return FontSizeToolComponent;
  }
}
